import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  MessageSquare, User, Clock, Send, X, AlertTriangle,
  CheckCircle, Mail, RefreshCw, Loader2, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminChats = () => {
  const [chats, setChats] = useState([]);
  const [escalatedChats, setEscalatedChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChats();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchChats, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedChat?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [allRes, escalatedRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/chats`, { headers }),
        fetch(`${API_URL}/api/admin/chats/escalated`, { headers })
      ]);
      
      if (allRes.ok) {
        const allChats = await allRes.json();
        setChats(allChats);
      }
      
      if (escalatedRes.ok) {
        const escalated = await escalatedRes.json();
        setEscalatedChats(escalated);
      }
    } catch (error) {
      console.error('Failed to fetch chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    setReplyMessage('');
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedChat) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/chats/${selectedChat.id}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: selectedChat.id,
          message: replyMessage
        })
      });
      
      if (response.ok) {
        toast.success('Reply sent');
        setReplyMessage('');
        
        // Update local state with new message
        const newMessage = {
          role: 'admin',
          content: replyMessage,
          timestamp: new Date().toISOString()
        };
        setSelectedChat(prev => ({
          ...prev,
          messages: [...prev.messages, newMessage]
        }));
        
        // Refresh chats
        fetchChats();
      } else {
        toast.error('Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleCloseChat = async (chatId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/chats/${chatId}/close`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Chat closed');
        setSelectedChat(null);
        fetchChats();
      }
    } catch (error) {
      toast.error('Failed to close chat');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      escalated: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      closed: 'bg-muted text-muted-foreground border-muted'
    };
    return styles[status] || styles.active;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const filteredChats = chats.filter(chat => 
    chat.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="admin-chats">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">
            Chat Management
          </h1>
          <p className="text-muted-foreground">
            Manage customer support conversations and escalations
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{chats.length}</p>
                <p className="text-xs text-muted-foreground">Total Chats</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-500">{escalatedChats.length}</p>
                <p className="text-xs text-muted-foreground">Escalated</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{chats.filter(c => c.status === 'active').length}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <X className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{chats.filter(c => c.status === 'closed').length}</p>
                <p className="text-xs text-muted-foreground">Closed</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Chat List */}
          <div className="lg:col-span-1">
            <Card className="bg-card h-[600px] flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg uppercase">Conversations</CardTitle>
                  <Button variant="ghost" size="sm" onClick={fetchChats}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 bg-secondary/50"
                  />
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-2">
                <Tabs defaultValue="escalated">
                  <TabsList className="w-full mb-2">
                    <TabsTrigger value="escalated" className="flex-1 text-xs">
                      Escalated ({escalatedChats.length})
                    </TabsTrigger>
                    <TabsTrigger value="all" className="flex-1 text-xs">
                      All ({filteredChats.length})
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="escalated" className="mt-0">
                    {escalatedChats.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                        <p>No escalated chats</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {escalatedChats.map((chat) => (
                          <ChatListItem
                            key={chat.id}
                            chat={chat}
                            isSelected={selectedChat?.id === chat.id}
                            onClick={() => handleSelectChat(chat)}
                            getStatusBadge={getStatusBadge}
                          />
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="all" className="mt-0">
                    {filteredChats.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2" />
                        <p>No conversations</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredChats.map((chat) => (
                          <ChatListItem
                            key={chat.id}
                            chat={chat}
                            isSelected={selectedChat?.id === chat.id}
                            onClick={() => handleSelectChat(chat)}
                            getStatusBadge={getStatusBadge}
                          />
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Chat View */}
          <div className="lg:col-span-2">
            <Card className="bg-card h-[600px] flex flex-col">
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <CardHeader className="pb-3 border-b border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold text-foreground">{selectedChat.user_name}</h3>
                          <p className="text-xs text-muted-foreground">{selectedChat.user_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={getStatusBadge(selectedChat.status)}>
                          {selectedChat.status}
                        </Badge>
                        {selectedChat.status !== 'closed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCloseChat(selectedChat.id)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  {/* Messages */}
                  <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                    {selectedChat.messages?.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            msg.role === 'user'
                              ? 'bg-secondary/50'
                              : msg.role === 'admin'
                              ? 'bg-green-500/20 border border-green-500/30'
                              : msg.role === 'system'
                              ? 'bg-yellow-500/20 border border-yellow-500/30 text-center w-full'
                              : 'bg-primary/20 border border-primary/30'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium capitalize text-muted-foreground">
                              {msg.role === 'assistant' ? 'AI Bot' : msg.role}
                            </span>
                            <span className="text-xs text-muted-foreground/60">
                              {formatTime(msg.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </CardContent>

                  {/* Reply Input */}
                  {selectedChat.status !== 'closed' && (
                    <div className="p-4 border-t border-border">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type your reply..."
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
                          className="flex-1 bg-secondary/50"
                          data-testid="admin-reply-input"
                        />
                        <Button
                          onClick={handleSendReply}
                          disabled={!replyMessage.trim() || sending}
                          className="bg-primary"
                          data-testid="admin-send-reply"
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Select a conversation to view</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Chat List Item Component
const ChatListItem = ({ chat, isSelected, onClick, getStatusBadge }) => {
  const lastMessage = chat.messages?.[chat.messages.length - 1];
  
  return (
    <div
      onClick={onClick}
      className={`p-3 rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'bg-primary/10 border border-primary/30' 
          : 'bg-secondary/30 hover:bg-secondary/50 border border-transparent'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-medium text-sm text-foreground truncate">{chat.user_name}</span>
        <Badge variant="outline" className={`text-[10px] ${getStatusBadge(chat.status)}`}>
          {chat.status}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground truncate">{chat.user_email}</p>
      {lastMessage && (
        <p className="text-xs text-muted-foreground/70 truncate mt-1">
          {lastMessage.role}: {lastMessage.content.substring(0, 50)}...
        </p>
      )}
      <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground/50">
        <Clock className="w-3 h-3" />
        <span>{new Date(chat.created_at).toLocaleDateString()}</span>
        {chat.messages?.length > 0 && (
          <span className="ml-auto">{chat.messages.length} messages</span>
        )}
      </div>
    </div>
  );
};

export default AdminChats;
